import type { Asset } from '@compass/client/types/asset';
import { AssetType } from '@compass/client/types/asset';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { type CallConsoleContext, callConsoleService } from '@/services/callConsole.service';

import { parseRichTranscript } from '../utils/richTranscript';
import { findDataChunk } from '../utils/wavClip';

/** Asset names the AI agent gives its console files in Arcus. */
const CONSOLE_ASSET_NAMES = {
  richTranscript: 'rich_transcript',
  sessionLog: 'session_log',
  userUtterances: 'user_utterances',
} as const;

const keyFor = (ctx: CallConsoleContext, ...rest: string[]) => [
  'callConsole',
  ctx.organizationId,
  ctx.programId,
  ctx.caseRecordId,
  ctx.callId,
  ...rest,
];

const findByName = (assets: Asset[], name: string) => assets.find(a => a.asset_name === name);

const findRecording = (assets: Asset[]) =>
  assets.find(
    a =>
      a.asset_type === AssetType.ASSET_TYPE_CALL_FULL_RECORDING ||
      (!a.asset_name && (a.content_type ?? '').startsWith('audio/')),
  );

/**
 * Loads a call's console files. The files are written once the call ends, so they
 * are cached for the life of the page.
 */
export function useCallConsole(ctx: CallConsoleContext, enabled: boolean) {
  const assetsQuery = useQuery({
    queryKey: keyFor(ctx, 'assets'),
    queryFn: () => callConsoleService.listAssets(ctx),
    enabled,
    staleTime: Infinity,
  });
  const assets = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);

  const richAsset = findByName(assets, CONSOLE_ASSET_NAMES.richTranscript);
  const transcriptQuery = useQuery({
    queryKey: keyFor(ctx, 'rich', richAsset?.id ?? ''),
    queryFn: () => callConsoleService.downloadText(ctx, richAsset!.id!),
    enabled: enabled && !!richAsset?.id,
    staleTime: Infinity,
  });
  const parsed = useMemo(
    () => (transcriptQuery.data ? parseRichTranscript(transcriptQuery.data) : null),
    [transcriptQuery.data],
  );

  return {
    assets,
    richAsset,
    logAsset: findByName(assets, CONSOLE_ASSET_NAMES.sessionLog),
    utterancesAsset: findByName(assets, CONSOLE_ASSET_NAMES.userUtterances),
    recordingAsset: findRecording(assets),
    parsed,
    isLoading: assetsQuery.isLoading || transcriptQuery.isLoading,
    error: assetsQuery.error ?? transcriptQuery.error,
  };
}

/** The session log, fetched only when the Log tab is opened. */
export function useSessionLog(ctx: CallConsoleContext, asset: Asset | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keyFor(ctx, 'log', asset?.id ?? ''),
    queryFn: () => callConsoleService.downloadText(ctx, asset!.id!),
    enabled: enabled && !!asset?.id,
    staleTime: Infinity,
  });
}

/** The user-utterance WAV, fetched on the first clip play. */
export function useUtteranceAudio(
  ctx: CallConsoleContext,
  asset: Asset | undefined,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: keyFor(ctx, 'utterances', asset?.id ?? ''),
    queryFn: () => callConsoleService.downloadBytes(ctx, asset!.id!),
    enabled: enabled && !!asset?.id,
    staleTime: Infinity,
  });
  const chunk = useMemo(() => (query.data ? findDataChunk(query.data) : null), [query.data]);
  return { buffer: query.data, chunk, isLoading: query.isLoading && enabled, error: query.error };
}

/** An object URL for the call recording; revoked when the page unmounts or the asset changes. */
export function useRecordingUrl(
  ctx: CallConsoleContext,
  asset: Asset | undefined,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: keyFor(ctx, 'recording', asset?.id ?? ''),
    queryFn: async () => {
      const bytes = await callConsoleService.downloadBytes(ctx, asset!.id!);
      return new Blob([bytes], { type: asset?.content_type || 'audio/mpeg' });
    },
    enabled: enabled && !!asset?.id,
    staleTime: Infinity,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) return undefined;
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);

  return { url, isLoading: query.isLoading && !!asset, error: query.error };
}
